'use client';
import { memo, useCallback, useRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { VStack, Input, InputGroup, InputRightElement, Textarea } from '@chakra-ui/react';

import LabelText from 'shared/LabelText';
import ErrorText from 'shared/ErrorText';
import NextImage from 'shared/NextImage';

import Cross from 'assets/icons/Cross.svg';
import wrapperInputStyle from './model/inputGroup.style';
import { scrollBarStyle } from './model/scrollBarStyle';

const TextField = ({
  label = '',
  hint = '',
  error = false,
  type = 'input',
  onChange,
  name = null,
  value,
  max,
  maxH,
  minH,
  mask,
  inputGroupProps = null,
  containerProps = null,
  trigger = null,
  ...props
}) => {
  const inputRef = useRef(null);

  const handleFocusInput = useCallback(() => inputRef.current?.focus(), []);

  const handleClear = useCallback(() => {
    inputRef.current.value = '';
    onChange(inputRef.current.value);
  }, [onChange]);

  const handleInputChange = useCallback(
    ({ target: { value } }) => {
      if (value.length > max) return;
      onChange(value);
    },
    [name, max],
  );

  const handlePhoneInputChange = useCallback(({ value }) => onChange(value), [name, max]);

  return (
    <VStack w="full" align="start" gap="0" position="relative" {...containerProps}>
      <LabelText value={value} label={label} />

      <InputGroup
        onClick={handleFocusInput}
        sx={wrapperInputStyle(type, error, inputGroupProps)}
        role="group"
      >
        {type === 'textarea' ? (
          <Textarea
            ref={inputRef}
            name={name}
            value={value}
            maxH={maxH}
            minH={minH}
            border="none"
            focusBorderColor="white"
            sx={scrollBarStyle}
            onInput={handleInputChange}
            {...props}
          />
        ) : mask ? (
          <NumericFormat
            getInputRef={inputRef}
            name={name}
            value={value}
            prefix="+"
            maxLength={max}
            customInput={Input}
            border="none"
            focusBorderColor="white"
            _hover={{ border: 'none' }}
            type="tel"
            onValueChange={handlePhoneInputChange}
            {...props}
          />
        ) : (
          <Input
            ref={inputRef}
            name={name}
            type={type}
            value={value}
            border="none"
            focusBorderColor="white"
            fontSize={{ laptop: '18px', tablet: '12px', mobile: '16px' }}
            onInput={handleInputChange}
            {...props}
          />
        )}

        <InputRightElement
          h="fit-content"
          role="group"
          pos="relative"
          onClick={handleClear}
          _groupFocusWithin={{ opacity: value.length ? 0.56 : 0 }}
          _groupHover={{ opacity: value.length ? 0.56 : 0 }}
          cursor={value?.length ? 'pointer' : 'default'}
        >
          <NextImage
            src={Cross}
            alt="Иконка крестика"
            w="auto"
            h="auto"
            boxSize={{ mobile: '18px', laptop: '24px' }}
          />
        </InputRightElement>
      </InputGroup>

      <ErrorText fieldName={name} error={error} hint={hint} />
    </VStack>
  );
};

export default memo(TextField);
